/**
 * IndexedDB storage lifecycle: persist request, quota monitoring,
 * versionchange/blocked messaging, optional-detail eviction, clear cache.
 */
import offlineDB, { invalidateOfflineCacheWrites } from '@/indexedDB/offlineDB.js';
import { checkForAppUpdate, isReloadPending } from '@/registerSW.js';
import { cancelPendingAuthoritativeSyncs } from '@/services/offlineRealtimeService.js';
import { cancelOfflineListSyncs, runOfflineListSync } from '@/services/offlineSyncService.js';
import { canMutateOnline, getServerBuildId, onServerBuildChange } from '@/services/onlineCheckService.js';
import { clearSearchCoverage } from '@/services/posSearchCoverage.js';

const PROTECTED_TABLES = new Set([
    'auth',
    'sale_drafts',
    // The holds behind those drafts, per POS session; rebuilt only by the
    // session that owns them, so a cache clear must not take them either.
    'cart_reservations',
]);

const OPTIONAL_DETAIL_TABLES = [
    // Fully rebuildable from server search responses; _cached_at drives the LRU.
    'pos_catalog',
    'sales_details',
    'transfer_details',
    'gate_pass_details',
    'order_request_details',
    'purchase_details',
    'branch_details',
    'supplier_details',
    'customer_details',
    'expense_details',
    'account_details',
    'automotive_details',
    'load_form_details',
    'delivery_person_details',
    'worker_details',
];

/**
 * Which deployment filled this browser's caches, as last reconciled.
 *
 * localStorage rather than `offline_sync_meta`, deliberately: the stamp has to
 * survive the very thing it triggers. Kept inside IndexedDB it would be wiped by
 * the clear it just ordered, and every boot would look like a fresh deployment.
 */
export const OFFLINE_BUILD_KEY = 'inventree_offline_build';

/**
 * Tables whose emptiness decides whether an unstamped browser is a new install
 * or one that predates this mechanism.
 *
 * `offline_sync_meta` alone would answer for the list caches, but a POS terminal
 * that never turned list syncing on still holds a branch catalogue, and
 * `pos_catalog` is written straight off search responses with no meta row of its
 * own. Any one of the three holding a row means "filled by a build we cannot
 * name", which is the case that has to be reset once.
 */
const BUILD_EVIDENCE_TABLES = ['offline_sync_meta', 'branch_products', 'pos_catalog'];

let initialized = false;
let quotaTimerId = null;
let stopBuildListener = null;
let versionChangeReloading = false;

function showPersistentBanner(message, id = 'inventree-idb-lifecycle') {
    if (typeof document === 'undefined') return;
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement('div');
        el.id = id;
        el.setAttribute('role', 'alert');
        el.style.cssText =
            'position:fixed;top:0;left:0;right:0;z-index:10000;background:#7c2d12;color:#fff;padding:10px 16px;text-align:center;font-size:14px;';
        document.body.appendChild(el);
    }
    el.textContent = message;
}

export async function requestPersistentStorage() {
    try {
        if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
            return await navigator.storage.persist();
        }
    } catch (e) {
        console.warn('[storageLifecycle] persist() failed', e);
    }
    return false;
}

export async function getStorageEstimate() {
    try {
        if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
        const est = await navigator.storage.estimate();
        const usage = est.usage ?? 0;
        const quota = est.quota ?? 0;
        const ratio = quota > 0 ? usage / quota : 0;
        return { usage, quota, ratio, percent: Math.round(ratio * 100) };
    } catch {
        return null;
    }
}

export async function warnIfNearQuota(threshold = 0.85) {
    const est = await getStorageEstimate();
    if (est && est.ratio >= threshold) {
        console.warn('[storageLifecycle] storage near quota', est);
        showPersistentBanner(
            `Offline storage is ${est.percent}% full. Oldest optional detail caches may be cleared. Consider clearing offline data in Settings.`,
            'inventree-idb-quota',
        );
        await evictOptionalDetails(Math.ceil((est.ratio - threshold) * 20) || 5);
    }
    return est;
}

function primaryKeyFor(table, row) {
    const keyPath = table.schema.primKey.keyPath;
    if (Array.isArray(keyPath)) return keyPath.map((key) => row[key]);
    return row[keyPath];
}

/** Remove a bounded oldest slice of optional detail rows (best-effort). */
/** Read time when we have it, write time for rows cached before v32. */
function lastUsedAt(row) {
    return Date.parse(row?._accessed_at ?? '') || Date.parse(row?._cached_at ?? '') || 0;
}

export async function evictOptionalDetails(maxTables = 3) {
    let affectedTables = 0;
    for (const name of OPTIONAL_DETAIL_TABLES) {
        if (affectedTables >= maxTables) break;
        const table = offlineDB[name];
        if (!table) continue;
        try {
            const rows = await table.toArray();
            if (!rows.length) continue;

            // "Make available offline" is a promise, and quota pressure is not
            // permission to break it. Pinned rows are not eviction candidates
            // at all — not merely last in line.
            const candidates = rows.filter((row) => !row?._pinned);
            if (!candidates.length) continue;

            // Least-recently-*used*, falling back to write time for rows cached
            // before v32 introduced the access stamp.
            candidates.sort((a, b) => lastUsedAt(a) - lastUsedAt(b));

            const removeCount = Math.max(1, Math.ceil(candidates.length * 0.25));
            const keys = candidates.slice(0, removeCount).map((row) => primaryKeyFor(table, row));
            await table.bulkDelete(keys);
            affectedTables += 1;
            console.info('[storageLifecycle] evicted least-recently-used rows', name, keys.length);
        } catch (e) {
            console.warn('[storageLifecycle] eviction failed', name, e);
        }
    }
    return affectedTables;
}

/**
 * Clear rebuildable offline caches. Protects auth and drafts.
 * Keeps only branch_products rows that still have cart reservations.
 * @param {{ includeProtected?: boolean }} [opts]
 */
export async function clearOfflineCaches({ includeProtected = false } = {}) {
    // Prevent a fetch that started before this user action from publishing a
    // completed snapshot immediately after the clear transaction.
    invalidateOfflineCacheWrites();
    // The catalogue is about to be wiped, so no keyword is covered any more.
    clearSearchCoverage();
    try {
        await cancelOfflineListSyncs();
        cancelPendingAuthoritativeSyncs();
    } catch (error) {
        console.warn('[storageLifecycle] could not cancel pending cache refreshes', error);
    }

    await offlineDB.open();
    const targets = offlineDB.tables.filter((table) => includeProtected || !PROTECTED_TABLES.has(table.name));
    try {
        await offlineDB.transaction('rw', targets, async () => {
            for (const table of targets) {
                if (table.name === 'branch_products' && !includeProtected) {
                    const rows = await table.toArray();
                    const dropKeys = rows
                        .filter((row) => !(Number(row?.reserved_qty) > 0))
                        .map((row) => row.key)
                        .filter((key) => key != null);
                    if (dropKeys.length) await table.bulkDelete(dropKeys);
                    continue;
                }
                await table.clear();
            }
        });
        return { ok: true, cleared: targets.map((table) => table.name), failed: [] };
    } catch (e) {
        console.error('[storageLifecycle] cache clear failed', e);
        return {
            ok: false,
            cleared: [],
            failed: targets.map((table) => table.name),
            error: e,
        };
    }
}

function readStoredBuildId() {
    try {
        return localStorage?.getItem(OFFLINE_BUILD_KEY) || null;
    } catch {
        return null;
    }
}

function writeStoredBuildId(buildId) {
    try {
        localStorage?.setItem(OFFLINE_BUILD_KEY, buildId);
    } catch {
        // Private-browsing or a full origin quota. The reconcile is idempotent,
        // so the worst case is that it runs again next boot.
    }
}

async function holdsCachedServerData() {
    for (const name of BUILD_EVIDENCE_TABLES) {
        const table = offlineDB[name];
        if (!table) continue;
        try {
            if ((await table.limit(1).count()) > 0) return true;
        } catch (e) {
            // An unreadable store is not evidence of an empty one. Treating it
            // as populated errs towards a reseed, which is recoverable; the
            // other way round leaves stale rows behind forever.
            console.warn('[storageLifecycle] could not probe cached data', name, e);
            return true;
        }
    }
    return false;
}

/**
 * Reconcile the offline caches with the deployment that is actually answering.
 *
 * This is the automatic form of what an operator otherwise does by hand after a
 * release: clear the offline database, reload, let it refill. It exists because
 * neither half of a deploy reaches an already-populated cache on its own —
 * Dexie's own `version()` upgrade only fires for a changed *key path*, so a
 * normalizer that starts writing a new field leaves every cached row exactly as
 * it was; and the watermark sync asks for `updated_at >= watermark`, which a
 * migration that backfills a column in raw SQL does not move. The rows are
 * stale, and nothing in the pipeline can tell.
 *
 * Clearing rather than reseeding in place, because the two failures above are
 * about row *shape*, and only a store the current build refills from scratch is
 * guaranteed to have it. `clearOfflineCaches` keeps the session, the POS draft
 * and any cart reservation, so what is thrown away is exactly what the server
 * can hand back.
 *
 * Runs before the app mounts (see app.js) so the convergence that follows the
 * login watch reseeds into an empty cache rather than being wiped by this.
 *
 * Doing nothing is the correct answer more often than not:
 * - no stamp from the server means no probe has succeeded yet. Clearing a cache
 *   while offline destroys the only copy of data there is;
 * - an unstamped *empty* cache is a fresh install, not a stale one;
 * - a matching stamp means this build filled it.
 */
export async function ensureOfflineCacheMatchesBuild() {
    const serverBuildId = getServerBuildId();
    if (!serverBuildId || !canMutateOnline()) return { ok: false, reason: 'build_unknown' };

    const storedBuildId = readStoredBuildId();
    if (storedBuildId === serverBuildId) return { ok: true, reason: 'current' };

    // No stamp is also every browser that upgraded *into* this mechanism, and
    // those hold rows written by a deployment nobody recorded. They are reset
    // once; a genuinely empty cache is simply stamped.
    if (storedBuildId === null && !(await holdsCachedServerData())) {
        writeStoredBuildId(serverBuildId);
        return { ok: true, reason: 'seeded', to: serverBuildId };
    }

    const result = await clearOfflineCaches({ includeProtected: false });
    if (!result.ok) return { ok: false, reason: 'clear_failed', error: result.error };

    // Stamped only once the clear committed, so a failed run is retried on the
    // next boot instead of being recorded as done.
    writeStoredBuildId(serverBuildId);
    console.info('[storageLifecycle] offline caches reset for a new deployment', {
        from: storedBuildId,
        to: serverBuildId,
    });
    return { ok: true, reason: 'reset', from: storedBuildId, to: serverBuildId };
}

/**
 * A deployment landed while this tab was open.
 *
 * Two different releases arrive through this one signal and they want opposite
 * things. If new assets are on their way, the service worker's own
 * `controllerchange` reloads the tab, and the reset belongs to the *next*
 * boot — clearing here would hand the rebuild to the outgoing bundle, whose
 * normalizers are precisely the ones the new build disagrees with, and the
 * reload would then throw that work away anyway.
 *
 * If no worker is coming, the bundle this tab is running is already the deployed
 * one and only the server side moved — a migration, a list payload that grew a
 * column. Nothing is going to reload, so the reset happens here, followed by the
 * reseed that a cleared `offline_sync_meta` turns into a full one.
 */
async function handleDeploymentChange() {
    try {
        if (isReloadPending() || (await checkForAppUpdate())) return;

        const reconciled = await ensureOfflineCacheMatchesBuild();
        if (reconciled.reason !== 'reset') return;

        await runOfflineListSync({ reason: 'deployment_change', mode: 'full', force: true });
    } catch (e) {
        console.warn('[storageLifecycle] deployment change handling failed', e);
    }
}

export function initStorageLifecycle() {
    if (initialized || typeof indexedDB === 'undefined') return;
    initialized = true;

    requestPersistentStorage();
    warnIfNearQuota();

    // A deployment noticed by the connectivity probe, mid-session.
    stopBuildListener = onServerBuildChange(() => handleDeploymentChange());

    // Dexie fires versionchange when another tab upgrades the DB.
    //
    // Closing the connection is mandatory — the upgrade in the other tab blocks
    // until every older one lets go. What used to follow it was a banner asking
    // for a reload, which left this tab with no offline database at all until
    // someone read the banner and acted: the schema moved because a newer build
    // is running elsewhere, so there is nothing here worth preserving and no
    // decision for the operator to make. The POS draft survives in `sale_drafts`
    // and is restored on the way back up.
    offlineDB.on('versionchange', () => {
        try {
            offlineDB.close();
        } catch {
            /* ignore */
        }
        if (versionChangeReloading) return;
        versionChangeReloading = true;
        showPersistentBanner('App updated — reloading…');
        if (typeof window !== 'undefined') window.location.reload();
    });

    offlineDB.on('blocked', () => {
        showPersistentBanner('Please close other Inventree tabs to finish the update.');
    });

    // Periodic quota check while app is open
    quotaTimerId = setInterval(() => {
        warnIfNearQuota().catch(() => {});
    }, 5 * 60 * 1000);
}

export function destroyStorageLifecycle() {
    if (quotaTimerId) {
        clearInterval(quotaTimerId);
        quotaTimerId = null;
    }
    if (typeof stopBuildListener === 'function') {
        stopBuildListener();
        stopBuildListener = null;
    }
    initialized = false;
}

export default {
    initStorageLifecycle,
    requestPersistentStorage,
    getStorageEstimate,
    warnIfNearQuota,
    evictOptionalDetails,
    clearOfflineCaches,
    ensureOfflineCacheMatchesBuild,
    destroyStorageLifecycle,
};
